import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, adminAuthApi } from '../services/api.js';

const CORE_TECHNOLOGIES = [
  { id: 'virtual-try-on', name: 'Virtual Try-On for Fashion & Clothing Industry', category: 'Fashion & Clothing' },
  { id: 'pms-hospitality', name: 'Property Management System (PMS)', category: 'Tourism & Hospitality' },
  { id: 'ecommerce-ai', name: 'eCommerce Platform', category: 'Consumer Retail' },
  { id: 'ai-research-dev', name: 'AI Research & Development', category: 'JOFFON R&D' },
  { id: 'custom', name: 'Custom / Other', category: 'Custom' },
];

const formatMoney = (value, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2 }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString();
};

const formatShortDate = (value) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getPaymentStatusTone = (status) => {
  if (status === 'COMPLETED') return 'success';
  if (status === 'APPROVED') return 'warning';
  if (status === 'CREATED') return 'info';
  return 'neutral';
};

const getInvoiceStatusTone = (status) => {
  if (status === 'PAID') return 'success';
  if (status === 'PENDING') return 'warning';
  return 'neutral';
};

const toInputDate = (date) => {
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 10);
};

const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const triggerCsvDownload = (rows, filename) => {
  const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.setAttribute('download', filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ── Icons ─────────────────────────────────────────────────────────────────────

const IconGrid = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 flex-shrink-0">
    <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
  </svg>
);
const IconCard = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 flex-shrink-0">
    <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
    <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
  </svg>
);
const IconPeople = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 flex-shrink-0">
    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
  </svg>
);
const IconShield = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 flex-shrink-0">
    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);
const IconCog = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 flex-shrink-0">
    <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
  </svg>
);
const IconReceipt = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 flex-shrink-0">
    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
  </svg>
);
const IconBuilding = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 flex-shrink-0">
    <path fillRule="evenodd" d="M4 2a1 1 0 00-1 1v14a1 1 0 001 1h3a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h3a1 1 0 001-1V3a1 1 0 00-1-1H4zm2 3a1 1 0 011-1h1a1 1 0 110 2H7a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2h-1zM6 8a1 1 0 011-1h1a1 1 0 110 2H7a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2h-1zM6 11a1 1 0 011-1h1a1 1 0 110 2H7a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2h-1z" clipRule="evenodd" />
  </svg>
);
const IconLogout = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 flex-shrink-0">
    <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
  </svg>
);
const IconMenu = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
    <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
  </svg>
);
const IconX = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);
const IconAlertTriangle = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
  </svg>
);
const IconChart = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 flex-shrink-0">
    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1H3a1 1 0 01-1-1v-6zM8 7a1 1 0 011-1h2a1 1 0 011 1v10a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 3a1 1 0 011-1h2a1 1 0 011 1v14a1 1 0 01-1 1h-2a1 1 0 01-1-1V3z" />
  </svg>
);

// ── Design-system class constants ─────────────────────────────────────────────
// One accent color (indigo) drives every primary action; semantic colors are
// reserved for status badges only. Keeps every tab visually consistent instead
// of each section inventing its own accent hue.

const cardClass = 'rounded-xl border border-slate-200 bg-white shadow-sm';
const inputClass = 'w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-colors focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20';
const selectClass = inputClass;
const textareaClass = `${inputClass} resize-none`;
const labelClass = 'block text-xs font-medium text-slate-500 mb-1.5';
const btnPrimary = 'inline-flex items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50';
const btnSecondary = 'inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50';
const btnDanger = 'inline-flex items-center justify-center gap-1.5 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50';
const btnGhostDanger = 'inline-flex items-center justify-center rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-100';
const btnGhostNeutral = 'inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50';
const tableWrapClass = 'overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm';
const theadRowClass = 'border-b border-slate-200 bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500';
const tbodyRowClass = 'border-b border-slate-100 last:border-0 hover:bg-slate-50/80 transition-colors';
const chipClass = (active) =>
  `rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
    active ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-300 bg-white text-slate-600 hover:border-indigo-300 hover:text-indigo-600'
  }`;

const BADGE_TONES = {
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  info: 'bg-blue-50 text-blue-700 border-blue-200',
  danger: 'bg-red-50 text-red-700 border-red-200',
  neutral: 'bg-slate-100 text-slate-600 border-slate-200',
};

const Badge = ({ tone = 'neutral', children }) => (
  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${BADGE_TONES[tone] || BADGE_TONES.neutral}`}>
    {children}
  </span>
);

const PageHeader = ({ title, subtitle, children }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
    <div>
      <h1 className="text-2xl font-semibold text-slate-900">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
    </div>
    {children && <div className="flex items-center gap-2">{children}</div>}
  </div>
);

const EmptyRow = ({ colSpan, loading, label }) => (
  <tr>
    <td colSpan={colSpan} className="px-4 py-16 text-center text-sm text-slate-400">
      {loading ? 'Loading…' : label}
    </td>
  </tr>
);

// ── Shared components ─────────────────────────────────────────────────────────

const Pagination = ({ page, totalPages, loading, onPrev, onNext }) => (
  <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
    <button type="button" onClick={onPrev} disabled={loading || page <= 1} className={btnSecondary}>
      ← Previous
    </button>
    <span className="text-slate-500 text-sm">Page {page} of {totalPages}</span>
    <button type="button" onClick={onNext} disabled={loading || page >= totalPages} className={btnSecondary}>
      Next →
    </button>
  </div>
);

const ConfirmModal = ({ title, description, confirmLabel, onCancel, onConfirm }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm px-4">
    <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-red-200 bg-red-50 text-red-600">
          <IconAlertTriangle />
        </div>
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      </div>
      <p className="text-sm text-slate-500 mb-6">{description}</p>
      <div className="flex gap-3">
        <button type="button" onClick={onCancel} className={`flex-1 ${btnSecondary}`}>
          No, keep it
        </button>
        <button type="button" onClick={onConfirm} className={`flex-1 ${btnDanger}`}>
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

const NAV_ITEMS = [
  { id: 'overview', label: 'Overview', Icon: IconGrid },
  { id: 'payments', label: 'Payments', Icon: IconCard },
  { id: 'users', label: 'Users', Icon: IconPeople },
  { id: 'companies', label: 'Companies', Icon: IconBuilding },
  { id: 'invoices', label: 'Invoices', Icon: IconReceipt },
  { id: 'audit', label: 'Audit Logs', Icon: IconShield },
  { id: 'settings', label: 'Settings', Icon: IconCog },
];

const RANGE_BTNS = [
  { id: 'today', label: 'Today', days: 0 },
  { id: '7d', label: '7 Days', days: 7 },
  { id: '30d', label: '30 Days', days: 30 },
  { id: '90d', label: '90 Days', days: 90 },
];

// ── Main component ────────────────────────────────────────────────────────────

const AdminPaymentsPage = () => {
  // Auth
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authUser, setAuthUser] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [passwordChangeMessage, setPasswordChangeMessage] = useState('');
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotOtpCode, setForgotOtpCode] = useState('');
  const [forgotNewPassword, setForgotNewPassword] = useState('');
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');

  // Navigation
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Global error
  const [error, setError] = useState('');

  // Overview
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [overviewFrom, setOverviewFrom] = useState('');
  const [overviewTo, setOverviewTo] = useState('');

  // Payments
  const [payments, setPayments] = useState([]);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [paymentsTotalPages, setPaymentsTotalPages] = useState(1);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('');
  const [paymentPayerType, setPaymentPayerType] = useState('');
  const [paymentSearch, setPaymentSearch] = useState('');
  const [paymentFrom, setPaymentFrom] = useState('');
  const [paymentTo, setPaymentTo] = useState('');

  // Users
  const [users, setUsers] = useState([]);
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [usersTotalCount, setUsersTotalCount] = useState(0);
  const [usersSearch, setUsersSearch] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);

  // Audit
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotalPages, setAuditTotalPages] = useState(1);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditIp, setAuditIp] = useState('');
  const [auditCountry, setAuditCountry] = useState('');
  const [auditLocation, setAuditLocation] = useState('');

  // Invoices
  const [invoices, setInvoices] = useState([]);
  const [invoicesPage, setInvoicesPage] = useState(1);
  const [invoicesTotalPages, setInvoicesTotalPages] = useState(1);
  const [invoicesTotalCount, setInvoicesTotalCount] = useState(0);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('');
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [invoiceFormLoading, setInvoiceFormLoading] = useState(false);
  const [confirmCancelId, setConfirmCancelId] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({
    userId: '', userEmail: '', companyName: '',
    productId: '', productName: '', productCategory: '',
    amount: '', description: '', billingType: 'ONE_TIME', dueDate: '', isRecurring: false,
    addFee: true, feePercent: '3.5',
  });
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [dropdownUsers, setDropdownUsers] = useState([]);
  const [dropdownUsersLoading, setDropdownUsersLoading] = useState(false);

  // Companies
  const [companies, setCompanies] = useState([]);
  const [companiesPage, setCompaniesPage] = useState(1);
  const [companiesTotalPages, setCompaniesTotalPages] = useState(1);
  const [companiesTotalCount, setCompaniesTotalCount] = useState(0);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companyStatusFilter, setCompanyStatusFilter] = useState('');
  const [companySearch, setCompanySearch] = useState('');
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [companyFormLoading, setCompanyFormLoading] = useState(false);
  const [editingCompanyId, setEditingCompanyId] = useState(null);
  const [confirmDeleteCompanyId, setConfirmDeleteCompanyId] = useState(null);
  const EMPTY_COMPANY_FORM = {
    companyName: '', registrationNumber: '', industry: '',
    email: '', phone: '', website: '', address: '',
    contactPerson: '', contactPersonTitle: '', notes: '', status: 'ACTIVE',
  };
  const [companyForm, setCompanyForm] = useState(EMPTY_COMPANY_FORM);

  // Settings
  const [twoFaSetupLoading, setTwoFaSetupLoading] = useState(false);
  const [twoFaVerifyLoading, setTwoFaVerifyLoading] = useState(false);
  const [twoFaSetupSecret, setTwoFaSetupSecret] = useState('');
  const [twoFaSetupUrl, setTwoFaSetupUrl] = useState('');
  const [twoFaCode, setTwoFaCode] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [profileEmail, setProfileEmail] = useState('');
  const [profilePhone, setProfilePhone] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminPhone, setNewAdminPhone] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [newAdminConfirmPassword, setNewAdminConfirmPassword] = useState('');
  const [createAdminLoading, setCreateAdminLoading] = useState(false);
  const [createAdminMessage, setCreateAdminMessage] = useState('');
  const [adminsList, setAdminsList] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [confirmRemoveAdminId, setConfirmRemoveAdminId] = useState(null);

  const maxDailyRevenue = useMemo(
    () => (summary?.daily?.length ? Math.max(...summary.daily.map((d) => d.totalRevenue || 0)) : 0),
    [summary]
  );

  const invoicePreviewAmount = Number(invoiceForm.amount) || 0;
  const invoicePreviewFeePercent = invoiceForm.addFee ? (Number(invoiceForm.feePercent) || 0) : 0;
  const invoicePreviewFeeAmount = Math.round(invoicePreviewAmount * invoicePreviewFeePercent) / 100;
  const invoicePreviewTotal = Math.round((invoicePreviewAmount + invoicePreviewFeeAmount) * 100) / 100;

  // ── Helpers ───────────────────────────────────────────────────────────────

  const requestWithRefresh = async (fn) => {
    try {
      return await fn();
    } catch (err) {
      if (err?.response?.status === 401) {
        try { await adminAuthApi.refresh(); } catch {}
        return fn();
      }
      throw err;
    }
  };

  const handleSessionExpired = () => {
    setIsAuthenticated(false);
    setAuthUser(null);
    setSummary(null);
    setPayments([]);
    setAuditLogs([]);
    setUsers([]);
    setError('Session expired. Please sign in again.');
  };

  const applyRange = (days, setFrom, setTo) => {
    const now = new Date();
    const toVal = toInputDate(now);
    if (days === 0) { setFrom(toVal); setTo(toVal); return; }
    const from = new Date(now);
    from.setDate(now.getDate() - (days - 1));
    setFrom(toInputDate(from));
    setTo(toVal);
  };

  // ── Data loaders ──────────────────────────────────────────────────────────

  const loadSummary = async (from = overviewFrom, to = overviewTo) => {
    setSummaryLoading(true);
    setError('');
    try {
      const res = await requestWithRefresh(() =>
        adminApi.getSummary({ from: from || undefined, to: to || undefined })
      );
      setSummary(res.data);
    } catch (err) {
      if (err?.response?.status === 401) handleSessionExpired();
      else setError(err.response?.data?.error || 'Failed to load overview');
    } finally {
      setSummaryLoading(false);
    }
  };

  const loadPayments = async (pg = 1) => {
    setPaymentsLoading(true);
    setError('');
    try {
      const res = await requestWithRefresh(() =>
        adminApi.getPayments({
          from: paymentFrom || undefined,
          to: paymentTo || undefined,
          status: paymentStatus || undefined,
          payerType: paymentPayerType || undefined,
          q: paymentSearch || undefined,
          page: pg,
          limit: 20,
        })
      );
      setPayments(res.data.payments || []);
      setPaymentsPage(res.data.page || 1);
      setPaymentsTotalPages(res.data.totalPages || 1);
    } catch (err) {
      if (err?.response?.status === 401) handleSessionExpired();
      else setError(err.response?.data?.error || 'Failed to load payments');
    } finally {
      setPaymentsLoading(false);
    }
  };

  const loadUsers = async (pg = 1) => {
    setUsersLoading(true);
    setError('');
    try {
      const res = await requestWithRefresh(() =>
        adminApi.getUsers({ page: pg, limit: 20, q: usersSearch || undefined })
      );
      setUsers(res.data.users || []);
      setUsersPage(res.data.page || 1);
      setUsersTotalPages(res.data.totalPages || 1);
      setUsersTotalCount(res.data.totalCount || 0);
    } catch (err) {
      if (err?.response?.status === 401) handleSessionExpired();
      else setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const loadInvoices = async (pg = 1) => {
    setInvoicesLoading(true);
    setError('');
    try {
      const res = await requestWithRefresh(() =>
        adminApi.getInvoices({
          page: pg, limit: 20,
          status: invoiceStatusFilter || undefined,
          q: invoiceSearch || undefined,
        })
      );
      setInvoices(res.data.invoices || []);
      setInvoicesPage(res.data.page || 1);
      setInvoicesTotalPages(res.data.totalPages || 1);
      setInvoicesTotalCount(res.data.totalCount || 0);
    } catch (err) {
      if (err?.response?.status === 401) handleSessionExpired();
      else setError(err.response?.data?.error || 'Failed to load invoices');
    } finally {
      setInvoicesLoading(false);
    }
  };

  const searchUsers = async (q) => {
    if (!q || q.length < 2) { setUserSearchResults([]); return; }
    setUserSearchLoading(true);
    try {
      const res = await requestWithRefresh(() => adminApi.getUsers({ q, limit: 10 }));
      setUserSearchResults(res.data.users || []);
    } catch {}
    finally { setUserSearchLoading(false); }
  };

  const loadDropdownUsers = async () => {
    setDropdownUsersLoading(true);
    try {
      const res = await requestWithRefresh(() => adminApi.getUsers({ limit: 100 }));
      setDropdownUsers(res.data.users || []);
    } catch {}
    finally { setDropdownUsersLoading(false); }
  };

  const selectUser = (u) => {
    setInvoiceForm((f) => ({ ...f, userId: u._id, userEmail: u.email, companyName: u.companyName || '' }));
    setUserSearchQuery(`${u.firstName} ${u.lastName} — ${u.email}`);
    setUserSearchResults([]);
  };

  const selectProduct = (tech) => {
    setInvoiceForm((f) => ({
      ...f,
      productId: tech.id,
      productName: tech.name,
      productCategory: tech.category,
    }));
  };

  const submitInvoice = async () => {
    if (!invoiceForm.userId) { setError('Select a user.'); return; }
    if (!invoiceForm.productName) { setError('Select a product.'); return; }
    if (!invoiceForm.amount || Number(invoiceForm.amount) <= 0) { setError('Enter a valid amount.'); return; }
    if (!invoiceForm.billingType) { setError('Select billing type.'); return; }
    setInvoiceFormLoading(true);
    setError('');
    try {
      await requestWithRefresh(() =>
        adminApi.createInvoice({
          userId: invoiceForm.userId,
          productName: invoiceForm.productName,
          productCategory: invoiceForm.productCategory,
          amount: Number(invoiceForm.amount),
          description: invoiceForm.description,
          billingType: invoiceForm.billingType,
          dueDate: invoiceForm.dueDate || undefined,
          isRecurring: invoiceForm.billingType === 'MONTHLY' ? invoiceForm.isRecurring : false,
          addFee: invoiceForm.addFee,
          feePercent: Number(invoiceForm.feePercent),
        })
      );
      setInvoiceForm({ userId: '', userEmail: '', companyName: '', productId: '', productName: '', productCategory: '', amount: '', description: '', billingType: 'ONE_TIME', dueDate: '', isRecurring: false, addFee: true, feePercent: '3.5' });
      setUserSearchQuery('');
      setShowCreateForm(false);
      await loadInvoices(1);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create invoice');
    } finally {
      setInvoiceFormLoading(false);
    }
  };

  const cancelInvoice = async (id) => {
    setConfirmCancelId(null);
    try {
      await requestWithRefresh(() => adminApi.cancelInvoice(id));
      setInvoices((prev) => prev.map((inv) => inv._id === id ? { ...inv, status: 'CANCELLED' } : inv));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel invoice');
    }
  };

  const loadCompanies = async (pg = 1) => {
    setCompaniesLoading(true);
    setError('');
    try {
      const res = await requestWithRefresh(() =>
        adminApi.getCompanies({
          page: pg, limit: 20,
          status: companyStatusFilter || undefined,
          q: companySearch || undefined,
        })
      );
      setCompanies(res.data.companies || []);
      setCompaniesPage(res.data.page || 1);
      setCompaniesTotalPages(res.data.totalPages || 1);
      setCompaniesTotalCount(res.data.totalCount || 0);
    } catch (err) {
      if (err?.response?.status === 401) handleSessionExpired();
      else setError(err.response?.data?.error || 'Failed to load companies');
    } finally {
      setCompaniesLoading(false);
    }
  };

  const toggleCreateCompanyForm = () => {
    setEditingCompanyId(null);
    setCompanyForm(EMPTY_COMPANY_FORM);
    setShowCompanyForm((v) => !v);
    setError('');
  };

  const startEditCompany = (company) => {
    setEditingCompanyId(company._id);
    setCompanyForm({
      companyName: company.companyName || '',
      registrationNumber: company.registrationNumber || '',
      industry: company.industry || '',
      email: company.email || '',
      phone: company.phone || '',
      website: company.website || '',
      address: company.address || '',
      contactPerson: company.contactPerson || '',
      contactPersonTitle: company.contactPersonTitle || '',
      notes: company.notes || '',
      status: company.status || 'ACTIVE',
    });
    setShowCompanyForm(true);
    setError('');
  };

  const startRegisterFromWebsite = (company) => {
    setEditingCompanyId(null);
    setCompanyForm({
      ...EMPTY_COMPANY_FORM,
      companyName: company.companyName || '',
      email: company.email || '',
      phone: company.phone || '',
      contactPerson: company.contactPerson || '',
    });
    setShowCompanyForm(true);
    setError('');
  };

  const submitCompany = async () => {
    if (!companyForm.companyName.trim()) { setError('Enter the company name.'); return; }
    if (!companyForm.registrationNumber.trim()) { setError('Enter the company registration number.'); return; }
    if (!companyForm.email.trim()) { setError('Enter a contact email.'); return; }
    if (!companyForm.contactPerson.trim()) { setError('Enter a contact person.'); return; }

    setCompanyFormLoading(true);
    setError('');
    try {
      if (editingCompanyId) {
        const res = await requestWithRefresh(() => adminApi.updateCompany(editingCompanyId, companyForm));
        setCompanies((prev) => prev.map((c) => (c._id === editingCompanyId ? res.data.company : c)));
      } else {
        await requestWithRefresh(() => adminApi.createCompany(companyForm));
        await loadCompanies(1);
      }
      setCompanyForm(EMPTY_COMPANY_FORM);
      setEditingCompanyId(null);
      setShowCompanyForm(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save company');
    } finally {
      setCompanyFormLoading(false);
    }
  };

  const deleteCompany = async (id) => {
    setConfirmDeleteCompanyId(null);
    try {
      await requestWithRefresh(() => adminApi.deleteCompany(id));
      setCompanies((prev) => prev.filter((c) => c._id !== id));
      setCompaniesTotalCount((n) => Math.max(n - 1, 0));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete company');
    }
  };

  const loadAuditLogs = async (pg = 1) => {
    setAuditLoading(true);
    setError('');
    try {
      const res = await requestWithRefresh(() =>
        adminApi.getAuditLogs({
          page: pg, limit: 15,
          ip: auditIp || undefined,
          country: auditCountry || undefined,
          location: auditLocation || undefined,
        })
      );
      setAuditLogs(res.data.logs || []);
      setAuditPage(res.data.page || 1);
      setAuditTotalPages(res.data.totalPages || 1);
    } catch (err) {
      if (err?.response?.status === 401) handleSessionExpired();
      else setError(err.response?.data?.error || 'Failed to load audit logs');
    } finally {
      setAuditLoading(false);
    }
  };

  // ── Auth actions ──────────────────────────────────────────────────────────

  const login = async () => {
    setAuthLoading(true);
    setError('');
    try {
      const res = await adminAuthApi.login({ username, password, otpCode: otpCode || undefined });
      setIsAuthenticated(true);
      setAuthUser({ userId: res.data.userId, username: res.data.username, email: res.data.email, phone: res.data.phone, twoFactorEnabled: res.data.twoFactorEnabled });
      setPassword('');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const exitForgotPasswordMode = () => {
    setForgotPasswordMode(false);
    setForgotPasswordStep(1);
    setForgotEmail('');
    setForgotOtpCode('');
    setForgotNewPassword('');
    setForgotConfirmPassword('');
    setForgotMessage('');
    setError('');
  };

  const sendForgotPasswordOtp = async () => {
    setError('');
    if (!forgotEmail) { setError('Enter your email.'); return; }
    setForgotLoading(true);
    try {
      const res = await adminAuthApi.forgotPassword({ email: forgotEmail });
      setForgotMessage(res.data.message || 'A reset code has been sent to your registered phone number.');
      setForgotPasswordStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reset code');
    } finally {
      setForgotLoading(false);
    }
  };

  const submitPasswordReset = async () => {
    setError('');
    setForgotMessage('');
    if (!forgotOtpCode || !forgotNewPassword || !forgotConfirmPassword) { setError('All fields required.'); return; }
    if (forgotNewPassword.length < 10) { setError('New password must be at least 10 characters.'); return; }
    if (forgotNewPassword !== forgotConfirmPassword) { setError('Passwords do not match.'); return; }
    setForgotLoading(true);
    try {
      const res = await adminAuthApi.resetPassword({ email: forgotEmail, otpCode: forgotOtpCode, newPassword: forgotNewPassword });
      setUsername(forgotEmail);
      exitForgotPasswordMode();
      setPasswordChangeMessage(res.data.message || 'Password reset. Please sign in with your new password.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setForgotLoading(false);
    }
  };

  const logout = async () => {
    try { await adminAuthApi.logout(); } catch {}
    setIsAuthenticated(false);
    setAuthUser(null);
    setSummary(null);
    setPayments([]);
    setAuditLogs([]);
    setUsers([]);
    setUsername('');
    setPassword('');
    setOtpCode('');
    setTwoFaSetupSecret('');
    setTwoFaSetupUrl('');
    setError('');
  };

  const setupTwoFactor = async () => {
    setTwoFaSetupLoading(true);
    setError('');
    try {
      const res = await requestWithRefresh(() => adminAuthApi.setupTwoFactor());
      setTwoFaSetupSecret(res.data.secret || '');
      setTwoFaSetupUrl(res.data.otpauthUrl || '');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to init 2FA');
    } finally {
      setTwoFaSetupLoading(false);
    }
  };

  const verifyTwoFactor = async () => {
    if (!twoFaCode) { setError('Enter OTP code.'); return; }
    setTwoFaVerifyLoading(true);
    setError('');
    try {
      await requestWithRefresh(() => adminAuthApi.verifyTwoFactor(twoFaCode));
      setAuthUser((prev) => ({ ...(prev || {}), twoFactorEnabled: true }));
      setTwoFaCode('');
      setTwoFaSetupSecret('');
      setTwoFaSetupUrl('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to verify 2FA');
    } finally {
      setTwoFaVerifyLoading(false);
    }
  };

  const changePassword = async () => {
    setError('');
    setPasswordChangeMessage('');
    if (!currentPassword || !newPassword || !confirmPassword) { setError('All fields required.'); return; }
    if (newPassword.length < 10) { setError('New password must be at least 10 characters.'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    setPasswordChangeLoading(true);
    try {
      await requestWithRefresh(() => adminAuthApi.changePassword({ currentPassword, newPassword }));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setIsAuthenticated(false);
      setAuthUser(null);
      setSummary(null);
      setPayments([]);
      setAuditLogs([]);
      setUsers([]);
      setPassword('');
      setOtpCode('');
      setPasswordChangeMessage('Password changed. Please sign in again.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  const updateProfile = async () => {
    setError('');
    setPasswordChangeMessage('');
    if (!profileEmail) { setError('Email is required.'); return; }
    setProfileLoading(true);
    try {
      await requestWithRefresh(() => adminAuthApi.updateProfile({ email: profileEmail, phone: profilePhone }));
      setIsAuthenticated(false);
      setAuthUser(null);
      setSummary(null);
      setPayments([]);
      setAuditLogs([]);
      setUsers([]);
      setUsername(profileEmail);
      setPassword('');
      setOtpCode('');
      setPasswordChangeMessage('Profile updated. Please sign in again with your new email.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const createAdmin = async () => {
    setError('');
    setCreateAdminMessage('');
    if (!newAdminEmail || !newAdminPhone || !newAdminPassword || !newAdminConfirmPassword) { setError('All fields required.'); return; }
    if (newAdminPassword.length < 10) { setError('Password must be at least 10 characters.'); return; }
    if (newAdminPassword !== newAdminConfirmPassword) { setError('Passwords do not match.'); return; }
    setCreateAdminLoading(true);
    try {
      await requestWithRefresh(() =>
        adminAuthApi.createAdmin({ email: newAdminEmail, phone: newAdminPhone, password: newAdminPassword })
      );
      setNewAdminEmail('');
      setNewAdminPhone('');
      setNewAdminPassword('');
      setNewAdminConfirmPassword('');
      setCreateAdminMessage('Admin account created successfully.');
      await loadAdmins();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create admin account');
    } finally {
      setCreateAdminLoading(false);
    }
  };

  const loadAdmins = async () => {
    setAdminsLoading(true);
    try {
      const res = await requestWithRefresh(() => adminAuthApi.listAdmins());
      setAdminsList(res.data.admins || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load admins');
    } finally {
      setAdminsLoading(false);
    }
  };

  const removeAdmin = async (id) => {
    setConfirmRemoveAdminId(null);
    try {
      await requestWithRefresh(() => adminAuthApi.removeAdmin(id));
      setAdminsList((prev) => prev.filter((a) => a._id !== id));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove admin');
    }
  };

  // ── CSV exports ───────────────────────────────────────────────────────────

  const exportPaymentsCsv = () => {
    const header = ['capturedAt', 'paypalOrderId', 'invoiceNumber', 'productName', 'productCategory', 'billingPreference', 'payerEmail', 'payerType', 'status', 'currency', 'amount'];
    const lines = [header.join(','), ...payments.map((r) =>
      [r.capturedAt, r.paypalOrderId, r.invoiceNumber, r.productName, r.productCategory, r.billingPreference, r.payerEmail, r.isGuest === false ? 'Registered' : 'Guest', r.status, r.currency, r.amount].map(escapeCsv).join(',')
    )];
    triggerCsvDownload(lines, `joffon-payments-${Date.now()}.csv`);
  };

  const exportUsersCsv = () => {
    const header = ['firstName', 'lastName', 'companyName', 'email', 'phone', 'isActive', 'createdAt'];
    const lines = [header.join(','), ...users.map((u) =>
      [u.firstName, u.lastName, u.companyName, u.email, u.phone || '', u.isActive ? 'Active' : 'Inactive', u.createdAt].map(escapeCsv).join(',')
    )];
    triggerCsvDownload(lines, `joffon-users-${Date.now()}.csv`);
  };

  const exportAuditCsv = async () => {
    try {
      const res = await requestWithRefresh(() =>
        adminApi.exportAuditLogs({ ip: auditIp || undefined, country: auditCountry || undefined, location: auditLocation || undefined })
      );
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', `admin-audit-${Date.now()}.csv`);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || 'Export failed');
    }
  };

  // ── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (authChecked) return;
    const check = async () => {
      try {
        const res = await adminAuthApi.getCurrentUser();
        if (res.data?.authenticated) {
          setIsAuthenticated(true);
          setAuthUser(res.data.user);
        }
      } catch {}
      finally { setAuthChecked(true); }
    };
    check();
  }, [authChecked]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (activeTab === 'overview' && !summary) loadSummary('', '');
    if (activeTab === 'payments' && payments.length === 0) loadPayments(1);
    if (activeTab === 'users' && users.length === 0) loadUsers(1);
    if (activeTab === 'companies' && companies.length === 0) loadCompanies(1);
    if (activeTab === 'invoices' && invoices.length === 0) loadInvoices(1);
    if (activeTab === 'audit' && auditLogs.length === 0) loadAuditLogs(1);
    if (activeTab === 'settings' && adminsList.length === 0) loadAdmins();
  }, [activeTab, isAuthenticated]); // eslint-disable-line

  useEffect(() => {
    if (showCreateForm && dropdownUsers.length === 0 && !dropdownUsersLoading) loadDropdownUsers();
  }, [showCreateForm]); // eslint-disable-line

  useEffect(() => {
    if (authUser) {
      setProfileEmail(authUser.email || authUser.username || '');
      setProfilePhone(authUser.phone || '');
    }
  }, [authUser]);

  // ── Render: session loading ───────────────────────────────────────────────

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex items-center gap-3 text-sm text-slate-500">
          <svg className="h-4 w-4 animate-spin text-indigo-500" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Checking session…
        </div>
      </div>
    );
  }

  // ── Render: login screen ──────────────────────────────────────────────────

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900 flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex flex-col items-center text-center">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-600 text-lg font-bold text-white shadow-sm mb-3">J</div>
            <p className="text-xl font-semibold tracking-tight text-slate-900">JOFFON</p>
            <p className="text-slate-400 text-xs uppercase tracking-widest mt-1">Admin Dashboard</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
            {!forgotPasswordMode ? (
              <>
                <h1 className="text-lg font-semibold mb-5 text-slate-900">Sign in</h1>
                <div className="space-y-3">
                  <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && login()} placeholder="Username" className={inputClass} />
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && login()} placeholder="Password" className={inputClass} />
                  <input type="text" value={otpCode} onChange={(e) => setOtpCode(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && login()} placeholder="2FA code (if enabled)" className={inputClass} />
                  <button type="button" onClick={login} disabled={authLoading} className={`w-full ${btnPrimary}`}>
                    {authLoading ? 'Signing in...' : 'Sign In'}
                  </button>
                  <button type="button" onClick={() => { exitForgotPasswordMode(); setForgotPasswordMode(true); }} className="w-full text-center text-xs text-indigo-600 hover:text-indigo-700 font-medium pt-1">
                    Forgot password?
                  </button>
                </div>
                {passwordChangeMessage && !error && <p className="mt-4 text-emerald-600 text-sm">{passwordChangeMessage}</p>}
                {error && <p className="mt-4 text-red-600 text-sm">{error}</p>}
              </>
            ) : (
              <>
                <h1 className="text-lg font-semibold mb-1 text-slate-900">Reset password</h1>
                <p className="text-slate-500 text-xs mb-5">
                  {forgotPasswordStep === 1
                    ? "We'll text a one-time code to your registered phone number."
                    : 'Enter the code we sent you and choose a new password.'}
                </p>
                {forgotPasswordStep === 1 ? (
                  <div className="space-y-3">
                    <input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && sendForgotPasswordOtp()} placeholder="Email" className={inputClass} />
                    <button type="button" onClick={sendForgotPasswordOtp} disabled={forgotLoading} className={`w-full ${btnPrimary}`}>
                      {forgotLoading ? 'Sending…' : 'Send Reset Code'}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <input type="text" value={forgotOtpCode} onChange={(e) => setForgotOtpCode(e.target.value)} placeholder="6-digit code" className={inputClass} />
                    <input type="password" value={forgotNewPassword} onChange={(e) => setForgotNewPassword(e.target.value)} placeholder="New password (min 10 chars)" className={inputClass} />
                    <input type="password" value={forgotConfirmPassword} onChange={(e) => setForgotConfirmPassword(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submitPasswordReset()} placeholder="Confirm new password" className={inputClass} />
                    <button type="button" onClick={submitPasswordReset} disabled={forgotLoading} className={`w-full ${btnPrimary}`}>
                      {forgotLoading ? 'Resetting…' : 'Reset Password'}
                    </button>
                  </div>
                )}
                <button type="button" onClick={exitForgotPasswordMode} className="w-full mt-3 text-center text-xs text-slate-500 hover:text-slate-700">
                  Back to Sign In
                </button>
                {forgotMessage && !error && <p className="mt-4 text-emerald-600 text-sm">{forgotMessage}</p>}
                {error && <p className="mt-4 text-red-600 text-sm">{error}</p>}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Render: dashboard ─────────────────────────────────────────────────────

  const navigateTo = (id) => {
    setActiveTab(id);
    setSidebarOpen(false);
    setError('');
  };

  return (
    <div className="flex h-screen bg-slate-50 text-slate-900 overflow-hidden">

      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-slate-900/30 z-20 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-30 w-64 flex-shrink-0 flex flex-col
        bg-white border-r border-slate-200 transition-transform duration-200
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Brand */}
        <div className="flex items-center gap-3 px-5 py-5 border-b border-slate-200">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-sm font-bold text-white">J</div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate">JOFFON</p>
            <p className="text-[11px] text-slate-400 uppercase tracking-widest">Admin</p>
          </div>
          <button type="button" onClick={() => setSidebarOpen(false)} className="lg:hidden ml-auto text-slate-400 hover:text-slate-700 p-1">
            <IconX />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {NAV_ITEMS.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => navigateTo(id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === id
                  ? 'bg-indigo-50 text-indigo-700'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              <Icon />
              {label}
            </button>
          ))}
        </nav>

        {/* Cross-links to other admin pages */}
        <div className="px-3 pb-2">
          <Link
            to="/admin/cse-market"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
          >
            <IconChart />
            CSE Market Data
          </Link>
        </div>

        {/* User + logout */}
        <div className="px-4 py-4 border-t border-slate-200">
          <p className="text-[11px] uppercase tracking-widest text-slate-400 mb-0.5">Signed in as</p>
          <p className="text-sm text-slate-700 font-medium mb-3 truncate">{authUser?.username}</p>
          <button
            type="button"
            onClick={logout}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
          >
            <IconLogout />
            Sign out
          </button>
        </div>
      </aside>

      {/* ── Content area ────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

        {/* Mobile top bar */}
        <header className="lg:hidden flex items-center gap-3 px-4 py-3 border-b border-slate-200 bg-white flex-shrink-0">
          <button type="button" onClick={() => setSidebarOpen(true)} className="text-slate-500 hover:text-slate-900">
            <IconMenu />
          </button>
          <span className="text-sm font-semibold text-slate-900">
            {NAV_ITEMS.find((n) => n.id === activeTab)?.label ?? 'Admin'}
          </span>
        </header>

        {/* Scrollable page */}
        <main className="flex-1 overflow-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">

            {/* Error banner */}
            {error && (
              <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm flex items-start justify-between gap-3">
                <span>{error}</span>
                <button type="button" onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0 mt-0.5"><IconX /></button>
              </div>
            )}

            {/* ════ OVERVIEW ════════════════════════════════════════════════ */}
            {activeTab === 'overview' && (
              <div>
                <PageHeader title="Overview" subtitle="Revenue & payment summary">
                  <button type="button" onClick={() => loadSummary(overviewFrom, overviewTo)} disabled={summaryLoading} className={btnPrimary}>
                    {summaryLoading ? 'Loading...' : 'Refresh'}
                  </button>
                </PageHeader>

                {/* Date filters */}
                <div className={`flex flex-wrap items-center gap-2 mb-6 p-4 ${cardClass}`}>
                  <input type="date" value={overviewFrom} onChange={(e) => setOverviewFrom(e.target.value)} className={`${inputClass} w-auto`} />
                  <input type="date" value={overviewTo} onChange={(e) => setOverviewTo(e.target.value)} className={`${inputClass} w-auto`} />
                  {RANGE_BTNS.map((btn) => (
                    <button key={btn.id} type="button" onClick={() => applyRange(btn.days, setOverviewFrom, setOverviewTo)} className={chipClass(false)}>
                      {btn.label}
                    </button>
                  ))}
                </div>

                {!summary ? (
                  <div className={`p-16 text-center ${cardClass}`}>
                    <p className="text-slate-400">{summaryLoading ? 'Loading...' : 'Click Refresh to load data.'}</p>
                  </div>
                ) : (
                  <>
                    {/* KPI row */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                      {[
                        { label: 'Total Revenue', value: formatMoney(summary.totalRevenue) },
                        { label: 'Total Payments', value: summary.totalPayments || 0 },
                        { label: 'Completed', value: summary.completedPayments || 0 },
                        { label: 'Avg Ticket', value: formatMoney(summary.averageTicket) },
                        { label: 'Completion Rate', value: `${(summary.completionRate || 0).toFixed(1)}%` },
                      ].map((kpi) => (
                        <div key={kpi.label} className={`p-4 ${cardClass}`}>
                          <p className="text-slate-500 text-xs font-medium mb-1">{kpi.label}</p>
                          <p className="text-2xl font-semibold text-slate-900">{kpi.value}</p>
                        </div>
                      ))}
                    </div>

                    {/* Charts */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      <div className={`p-5 ${cardClass}`}>
                        <h3 className="text-xs uppercase tracking-widest text-slate-400 font-medium mb-4">Daily Revenue</h3>
                        <div className="space-y-2.5 max-h-72 overflow-auto pr-1">
                          {!summary.daily?.length && <p className="text-slate-400 text-sm">No data.</p>}
                          {summary.daily?.map((item) => {
                            const pct = maxDailyRevenue ? (item.totalRevenue / maxDailyRevenue) * 100 : 0;
                            return (
                              <div key={item._id}>
                                <div className="flex justify-between text-xs text-slate-500 mb-1">
                                  <span>{formatShortDate(item._id)}</span>
                                  <span>{formatMoney(item.totalRevenue)}</span>
                                </div>
                                <div className="h-1.5 w-full rounded-full bg-slate-100">
                                  <div className="h-full rounded-full bg-indigo-500" style={{ width: `${Math.max(pct, 3)}%` }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className={`p-5 ${cardClass}`}>
                        <h3 className="text-xs uppercase tracking-widest text-slate-400 font-medium mb-4">Status Breakdown</h3>
                        <div className="space-y-2">
                          {!summary.statusBreakdown?.length && <p className="text-slate-400 text-sm">No data.</p>}
                          {summary.statusBreakdown?.map((s) => (
                            <div key={s._id || 'UNKNOWN'} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                              <div className="flex items-center justify-between mb-1">
                                <Badge tone={getPaymentStatusTone(s._id)}>{s._id || 'UNKNOWN'}</Badge>
                                <span className="text-slate-500 text-xs">{s.count} payments</span>
                              </div>
                              <p className="text-slate-900 font-semibold text-sm">{formatMoney(s.total)}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className={`p-5 ${cardClass}`}>
                        <h3 className="text-xs uppercase tracking-widest text-slate-400 font-medium mb-4">Top Categories</h3>
                        <div className="space-y-2">
                          {!summary.categoryBreakdown?.length && <p className="text-slate-400 text-sm">No data.</p>}
                          {summary.categoryBreakdown?.map((c) => (
                            <div key={c._id || 'Other'} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-slate-700 text-sm">{c._id || 'Other'}</span>
                                <span className="text-slate-400 text-xs">{c.count} orders</span>
                              </div>
                              <p className="text-indigo-600 font-semibold text-sm">{formatMoney(c.total)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ════ PAYMENTS ════════════════════════════════════════════════ */}
            {activeTab === 'payments' && (
              <div>
                <PageHeader title="Payments" subtitle="Captured transaction records">
                  <button type="button" onClick={exportPaymentsCsv} disabled={!payments.length} className={btnSecondary}>
                    Export CSV
                  </button>
                </PageHeader>

                <div className={`p-4 mb-5 ${cardClass}`}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                    <input type="date" value={paymentFrom} onChange={(e) => setPaymentFrom(e.target.value)} className={inputClass} />
                    <input type="date" value={paymentTo} onChange={(e) => setPaymentTo(e.target.value)} className={inputClass} />
                    <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)} className={selectClass}>
                      <option value="">All Statuses</option>
                      <option value="COMPLETED">COMPLETED</option>
                      <option value="APPROVED">APPROVED</option>
                      <option value="CREATED">CREATED</option>
                    </select>
                    <input type="text" value={paymentSearch} onChange={(e) => setPaymentSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && loadPayments(1)} placeholder="Search order, email, product…" className={inputClass} />
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="text-xs text-slate-400 uppercase tracking-widest font-medium">Payer Type</span>
                    {[
                      { id: '', label: 'All' },
                      { id: 'guest', label: 'Guest (no login)' },
                      { id: 'registered', label: 'Registered' },
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setPaymentPayerType(opt.id)}
                        className={chipClass(paymentPayerType === opt.id)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {RANGE_BTNS.map((btn) => (
                      <button key={btn.id} type="button" onClick={() => applyRange(btn.days, setPaymentFrom, setPaymentTo)} className={chipClass(false)}>
                        {btn.label}
                      </button>
                    ))}
                    <button type="button" onClick={() => loadPayments(1)} disabled={paymentsLoading} className={`ml-auto ${btnPrimary}`}>
                      {paymentsLoading ? 'Loading…' : 'Apply'}
                    </button>
                  </div>
                </div>

                <div className={tableWrapClass}>
                  <table className="w-full min-w-[860px] text-sm">
                    <thead>
                      <tr className={theadRowClass}>
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3">Order ID</th>
                        <th className="px-4 py-3">Invoice</th>
                        <th className="px-4 py-3">Product</th>
                        <th className="px-4 py-3">Payer</th>
                        <th className="px-4 py-3">Type</th>
                        <th className="px-4 py-3">Billing</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!payments.length && <EmptyRow colSpan={9} loading={paymentsLoading} label="No payments found." />}
                      {payments.map((p) => (
                        <tr key={p._id} className={tbodyRowClass}>
                          <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(p.capturedAt)}</td>
                          <td className="px-4 py-3 font-mono text-slate-500 text-xs">{p.paypalOrderId}</td>
                          <td className="px-4 py-3 font-mono text-slate-400 text-xs">{p.invoiceNumber || '-'}</td>
                          <td className="px-4 py-3 text-slate-900">{p.productName || p.productCategory || '-'}</td>
                          <td className="px-4 py-3 text-slate-700">{p.payerEmail || '-'}</td>
                          <td className="px-4 py-3">
                            <Badge tone={p.isGuest === false ? 'info' : 'warning'}>{p.isGuest === false ? 'Registered' : 'Guest'}</Badge>
                          </td>
                          <td className="px-4 py-3 text-slate-500 text-xs">{p.billingPreference || 'ONE_TIME'}</td>
                          <td className="px-4 py-3"><Badge tone={getPaymentStatusTone(p.status)}>{p.status}</Badge></td>
                          <td className="px-4 py-3 text-slate-900 font-semibold">{formatMoney(p.amount, p.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination page={paymentsPage} totalPages={paymentsTotalPages} loading={paymentsLoading} onPrev={() => loadPayments(Math.max(paymentsPage - 1, 1))} onNext={() => loadPayments(Math.min(paymentsPage + 1, paymentsTotalPages))} />
              </div>
            )}

            {/* ════ USERS ═══════════════════════════════════════════════════ */}
            {activeTab === 'users' && (
              <div>
                <PageHeader title="Users" subtitle={`${usersTotalCount} registered accounts`}>
                  <button type="button" onClick={exportUsersCsv} disabled={!users.length} className={btnSecondary}>
                    Export CSV
                  </button>
                </PageHeader>

                <div className="flex gap-3 mb-5">
                  <input type="text" value={usersSearch} onChange={(e) => setUsersSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && loadUsers(1)} placeholder="Search name, email, company…" className={`flex-1 ${inputClass}`} />
                  <button type="button" onClick={() => loadUsers(1)} disabled={usersLoading} className={btnPrimary}>
                    {usersLoading ? 'Loading…' : 'Search'}
                  </button>
                </div>

                <div className={tableWrapClass}>
                  <table className="w-full min-w-[720px] text-sm">
                    <thead>
                      <tr className={theadRowClass}>
                        <th className="px-4 py-3">Full Name</th>
                        <th className="px-4 py-3">Company</th>
                        <th className="px-4 py-3">Email</th>
                        <th className="px-4 py-3">Phone</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!users.length && <EmptyRow colSpan={6} loading={usersLoading} label="No users found." />}
                      {users.map((u) => (
                        <tr key={u._id} className={tbodyRowClass}>
                          <td className="px-4 py-3 text-slate-900 font-medium">{u.firstName} {u.lastName}</td>
                          <td className="px-4 py-3 text-slate-700">{u.companyName || '-'}</td>
                          <td className="px-4 py-3 text-slate-700">{u.email}</td>
                          <td className="px-4 py-3 text-slate-500">{u.phone || '-'}</td>
                          <td className="px-4 py-3">
                            <Badge tone={u.isActive ? 'success' : 'danger'}>{u.isActive ? 'Active' : 'Inactive'}</Badge>
                          </td>
                          <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(u.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {usersTotalPages > 1 && (
                  <Pagination page={usersPage} totalPages={usersTotalPages} loading={usersLoading} onPrev={() => loadUsers(Math.max(usersPage - 1, 1))} onNext={() => loadUsers(Math.min(usersPage + 1, usersTotalPages))} />
                )}
              </div>
            )}

            {/* ════ INVOICES ════════════════════════════════════════════════ */}
            {activeTab === 'invoices' && (
              <div>
                <PageHeader title="Invoices" subtitle={`${invoicesTotalCount} total invoices`}>
                  <button
                    type="button"
                    onClick={() => { setShowCreateForm((v) => !v); setError(''); }}
                    className={btnPrimary}
                  >
                    {showCreateForm ? 'Cancel' : '+ Create Invoice'}
                  </button>
                </PageHeader>

                {/* ── Create form ── */}
                {showCreateForm && (
                  <div className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-6 mb-6">
                    <h2 className="text-sm font-semibold text-indigo-700 uppercase tracking-widest mb-5">New Invoice</h2>

                    {/* User search */}
                    <div className="mb-4">
                      <label className={labelClass}>Select User</label>
                      <select
                        value={invoiceForm.userId || ''}
                        onChange={(e) => {
                          const selected = dropdownUsers.find((u) => u._id === e.target.value);
                          if (selected) selectUser(selected);
                        }}
                        className={`${selectClass} mb-2`}
                      >
                        <option value="">
                          {dropdownUsersLoading ? 'Loading registered users…' : '— Pick a registered user —'}
                        </option>
                        {dropdownUsers.map((u) => (
                          <option key={u._id} value={u._id}>
                            {u.firstName} {u.lastName} — {u.email}{u.companyName ? ` (${u.companyName})` : ''}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-slate-500 mb-2">Or search by name/email below:</p>
                      <div className="relative">
                        <input
                          type="text"
                          value={userSearchQuery}
                          onChange={(e) => { setUserSearchQuery(e.target.value); searchUsers(e.target.value); }}
                          placeholder="Type name or email..."
                          className={inputClass}
                        />
                        {userSearchLoading && <p className="text-xs text-slate-500 mt-1">Searching...</p>}
                        {userSearchResults.length > 0 && (
                          <div className="absolute z-10 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg">
                            {userSearchResults.map((u) => (
                              <button
                                key={u._id}
                                type="button"
                                onClick={() => selectUser(u)}
                                className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0"
                              >
                                <span className="text-slate-900 font-medium">{u.firstName} {u.lastName}</span>
                                <span className="text-slate-500 ml-2">— {u.email}</span>
                                {u.companyName && <span className="text-indigo-600 ml-2 text-xs">({u.companyName})</span>}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {invoiceForm.userEmail && (
                        <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                          Selected: <strong>{invoiceForm.companyName || invoiceForm.userEmail}</strong> ({invoiceForm.userEmail})
                        </div>
                      )}
                    </div>

                    {/* Product selection */}
                    <div className="mb-4">
                      <label className={labelClass}>Product / Service</label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {CORE_TECHNOLOGIES.map((tech) => (
                          <button
                            key={tech.id}
                            type="button"
                            onClick={() => selectProduct(tech)}
                            className={`rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${
                              invoiceForm.productId === tech.id
                                ? 'border-indigo-600 bg-indigo-600 text-white'
                                : 'border-slate-200 bg-white text-slate-600 hover:border-indigo-300'
                            }`}
                          >
                            <p className="font-medium leading-snug">{tech.name}</p>
                            <p className={`text-xs mt-0.5 ${invoiceForm.productId === tech.id ? 'opacity-80' : 'opacity-60'}`}>{tech.category}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Amount, billing, description, due date */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                      <div>
                        <label className={labelClass}>Amount (USD) *</label>
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={invoiceForm.amount}
                          onChange={(e) => setInvoiceForm((f) => ({ ...f, amount: e.target.value }))}
                          placeholder="0.00"
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Billing Type *</label>
                        <select
                          value={invoiceForm.billingType}
                          onChange={(e) => setInvoiceForm((f) => ({ ...f, billingType: e.target.value }))}
                          className={selectClass}
                        >
                          <option value="ONE_TIME">One-time</option>
                          <option value="WEEKLY">Weekly</option>
                          <option value="MONTHLY">Monthly</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelClass}>Due Date</label>
                        <input
                          type="date"
                          value={invoiceForm.dueDate}
                          onChange={(e) => setInvoiceForm((f) => ({ ...f, dueDate: e.target.value }))}
                          className={inputClass}
                        />
                      </div>
                      <div className="sm:col-span-2 lg:col-span-1 flex items-end">
                        <button
                          type="button"
                          onClick={submitInvoice}
                          disabled={invoiceFormLoading}
                          className={`w-full ${btnPrimary}`}
                        >
                          {invoiceFormLoading ? 'Creating…' : 'Create Invoice'}
                        </button>
                      </div>
                    </div>

                    {/* Processing fee */}
                    <div className="mb-4 rounded-lg border border-slate-200 bg-white p-3">
                      <label className="flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={invoiceForm.addFee}
                          onChange={(e) => setInvoiceForm((f) => ({ ...f, addFee: e.target.checked }))}
                          className="h-4 w-4 rounded border-slate-300 accent-indigo-600"
                        />
                        Add processing fee
                      </label>
                      {invoiceForm.addFee && (
                        <div className="mt-3 flex flex-wrap items-end gap-4">
                          <div>
                            <label className={labelClass}>Fee (%)</label>
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              value={invoiceForm.feePercent}
                              onChange={(e) => setInvoiceForm((f) => ({ ...f, feePercent: e.target.value }))}
                              className={`w-28 ${inputClass}`}
                            />
                          </div>
                          <div className="text-xs text-slate-500 space-y-0.5">
                            <p>Amount: <span className="text-slate-900">${invoicePreviewAmount.toFixed(2)}</span></p>
                            <p>Fee: <span className="text-slate-900">${invoicePreviewFeeAmount.toFixed(2)}</span></p>
                            <p>Total to pay: <span className="font-semibold text-indigo-600">${invoicePreviewTotal.toFixed(2)}</span></p>
                          </div>
                        </div>
                      )}
                    </div>

                    {invoiceForm.billingType === 'MONTHLY' && (
                      <label className="mb-4 flex items-center gap-2.5 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 cursor-pointer hover:border-indigo-300">
                        <input
                          type="checkbox"
                          checked={invoiceForm.isRecurring}
                          onChange={(e) => setInvoiceForm((f) => ({ ...f, isRecurring: e.target.checked }))}
                          className="h-4 w-4 rounded border-slate-300 accent-indigo-600"
                        />
                        Auto-create a new invoice every month
                        <span className="text-xs text-slate-400">(stops if this invoice is cancelled)</span>
                      </label>
                    )}

                    <div>
                      <label className={labelClass}>Description</label>
                      <textarea
                        rows={2}
                        value={invoiceForm.description}
                        onChange={(e) => setInvoiceForm((f) => ({ ...f, description: e.target.value }))}
                        placeholder="Optional notes for the user…"
                        className={textareaClass}
                      />
                    </div>
                  </div>
                )}

                {/* Filters */}
                <div className="flex flex-wrap gap-3 mb-5">
                  <input
                    type="text"
                    value={invoiceSearch}
                    onChange={(e) => setInvoiceSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && loadInvoices(1)}
                    placeholder="Search invoice, email, company…"
                    className={`flex-1 min-w-[200px] ${inputClass}`}
                  />
                  <select
                    value={invoiceStatusFilter}
                    onChange={(e) => setInvoiceStatusFilter(e.target.value)}
                    className={`w-auto ${selectClass}`}
                  >
                    <option value="">All Statuses</option>
                    <option value="PENDING">PENDING</option>
                    <option value="PAID">PAID</option>
                    <option value="CANCELLED">CANCELLED</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => loadInvoices(1)}
                    disabled={invoicesLoading}
                    className={btnPrimary}
                  >
                    {invoicesLoading ? 'Loading…' : 'Apply'}
                  </button>
                </div>

                {/* Invoice table */}
                <div className={tableWrapClass}>
                  <table className="w-full min-w-[860px] text-sm">
                    <thead>
                      <tr className={theadRowClass}>
                        <th className="px-4 py-3">Invoice #</th>
                        <th className="px-4 py-3">Company / Email</th>
                        <th className="px-4 py-3">Product</th>
                        <th className="px-4 py-3">Billing</th>
                        <th className="px-4 py-3">Amount</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Due Date</th>
                        <th className="px-4 py-3">Created</th>
                        <th className="px-4 py-3">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!invoices.length && <EmptyRow colSpan={9} loading={invoicesLoading} label="No invoices found." />}
                      {invoices.map((inv) => (
                        <tr key={inv._id} className={tbodyRowClass}>
                          <td className="px-4 py-3 font-mono text-indigo-600 text-xs">{inv.invoiceNumber}</td>
                          <td className="px-4 py-3">
                            <p className="text-slate-900 text-xs font-medium">{inv.companyName || '—'}</p>
                            <p className="text-slate-500 text-xs">{inv.userEmail}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-slate-900 text-xs">{inv.productName}</p>
                            <p className="text-slate-400 text-xs">{inv.productCategory}</p>
                          </td>
                          <td className="px-4 py-3 text-slate-500 text-xs">
                            {inv.billingType}
                            {inv.isRecurring && <span className="ml-1.5"><Badge tone="info">AUTO</Badge></span>}
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-slate-900 font-semibold">${Number(inv.totalAmount ?? inv.amount).toFixed(2)}</p>
                            {inv.feeAmount > 0 && (
                              <p className="text-slate-400 text-xs">${Number(inv.amount).toFixed(2)} +{inv.feePercent}% fee</p>
                            )}
                          </td>
                          <td className="px-4 py-3"><Badge tone={getInvoiceStatusTone(inv.status)}>{inv.status}</Badge></td>
                          <td className="px-4 py-3 text-slate-500 text-xs">{inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : '—'}</td>
                          <td className="px-4 py-3 text-slate-500 text-xs">{new Date(inv.createdAt).toLocaleDateString()}</td>
                          <td className="px-4 py-3">
                            {inv.status === 'PENDING' && (
                              <button
                                type="button"
                                onClick={() => setConfirmCancelId(inv._id)}
                                className={btnGhostDanger}
                              >
                                Cancel
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {invoicesTotalPages > 1 && (
                  <Pagination
                    page={invoicesPage}
                    totalPages={invoicesTotalPages}
                    loading={invoicesLoading}
                    onPrev={() => loadInvoices(Math.max(invoicesPage - 1, 1))}
                    onNext={() => loadInvoices(Math.min(invoicesPage + 1, invoicesTotalPages))}
                  />
                )}
              </div>
            )}

            {/* ════ COMPANIES ══════════════════════════════════════════════ */}
            {activeTab === 'companies' && (
              <div>
                <PageHeader title="Companies" subtitle={`${companiesTotalCount} companies — registered & website signups`}>
                  <button
                    type="button"
                    onClick={toggleCreateCompanyForm}
                    className={btnPrimary}
                  >
                    {showCompanyForm ? 'Cancel' : '+ Register Company'}
                  </button>
                </PageHeader>

                {/* ── Register / Edit form ── */}
                {showCompanyForm && (
                  <div className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-6 mb-6">
                    <h2 className="text-sm font-semibold text-indigo-700 uppercase tracking-widest mb-5">
                      {editingCompanyId ? 'Edit Company' : 'New Company'}
                    </h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                      <div>
                        <label className={labelClass}>Company Name *</label>
                        <input
                          type="text"
                          value={companyForm.companyName}
                          onChange={(e) => setCompanyForm((f) => ({ ...f, companyName: e.target.value }))}
                          placeholder="Acme Holdings (Pvt) Ltd"
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Registration Number *</label>
                        <input
                          type="text"
                          value={companyForm.registrationNumber}
                          onChange={(e) => setCompanyForm((f) => ({ ...f, registrationNumber: e.target.value }))}
                          placeholder="PV 00123456"
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Industry</label>
                        <input
                          type="text"
                          value={companyForm.industry}
                          onChange={(e) => setCompanyForm((f) => ({ ...f, industry: e.target.value }))}
                          placeholder="Fashion & Clothing"
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Email *</label>
                        <input
                          type="email"
                          value={companyForm.email}
                          onChange={(e) => setCompanyForm((f) => ({ ...f, email: e.target.value }))}
                          placeholder="contact@company.com"
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Phone</label>
                        <input
                          type="text"
                          value={companyForm.phone}
                          onChange={(e) => setCompanyForm((f) => ({ ...f, phone: e.target.value }))}
                          placeholder="+94 71 234 5678"
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Website</label>
                        <input
                          type="text"
                          value={companyForm.website}
                          onChange={(e) => setCompanyForm((f) => ({ ...f, website: e.target.value }))}
                          placeholder="https://company.com"
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Contact Person *</label>
                        <input
                          type="text"
                          value={companyForm.contactPerson}
                          onChange={(e) => setCompanyForm((f) => ({ ...f, contactPerson: e.target.value }))}
                          placeholder="Full name"
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Contact Person Title</label>
                        <input
                          type="text"
                          value={companyForm.contactPersonTitle}
                          onChange={(e) => setCompanyForm((f) => ({ ...f, contactPersonTitle: e.target.value }))}
                          placeholder="e.g. Operations Manager"
                          className={inputClass}
                        />
                      </div>
                      {editingCompanyId && (
                        <div>
                          <label className={labelClass}>Status</label>
                          <select
                            value={companyForm.status}
                            onChange={(e) => setCompanyForm((f) => ({ ...f, status: e.target.value }))}
                            className={selectClass}
                          >
                            <option value="ACTIVE">ACTIVE</option>
                            <option value="INACTIVE">INACTIVE</option>
                          </select>
                        </div>
                      )}
                    </div>

                    <div className="mb-4">
                      <label className={labelClass}>Address</label>
                      <input
                        type="text"
                        value={companyForm.address}
                        onChange={(e) => setCompanyForm((f) => ({ ...f, address: e.target.value }))}
                        placeholder="Street, City, Country"
                        className={inputClass}
                      />
                    </div>

                    <div className="mb-5">
                      <label className={labelClass}>Notes</label>
                      <textarea
                        rows={2}
                        value={companyForm.notes}
                        onChange={(e) => setCompanyForm((f) => ({ ...f, notes: e.target.value }))}
                        placeholder="Optional internal notes…"
                        className={textareaClass}
                      />
                    </div>

                    <button
                      type="button"
                      onClick={submitCompany}
                      disabled={companyFormLoading}
                      className={`w-full sm:w-auto ${btnPrimary}`}
                    >
                      {companyFormLoading ? 'Saving…' : editingCompanyId ? 'Save Changes' : 'Register Company'}
                    </button>
                  </div>
                )}

                {/* Filters */}
                <div className="flex flex-wrap gap-3 mb-5">
                  <input
                    type="text"
                    value={companySearch}
                    onChange={(e) => setCompanySearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && loadCompanies(1)}
                    placeholder="Search company, reg. no., email, contact…"
                    className={`flex-1 min-w-[200px] ${inputClass}`}
                  />
                  <select
                    value={companyStatusFilter}
                    onChange={(e) => setCompanyStatusFilter(e.target.value)}
                    className={`w-auto ${selectClass}`}
                  >
                    <option value="">All Statuses</option>
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => loadCompanies(1)}
                    disabled={companiesLoading}
                    className={btnPrimary}
                  >
                    {companiesLoading ? 'Loading…' : 'Apply'}
                  </button>
                </div>

                {/* Company table */}
                <div className={tableWrapClass}>
                  <table className="w-full min-w-[1020px] text-sm">
                    <thead>
                      <tr className={theadRowClass}>
                        <th className="px-4 py-3">Company</th>
                        <th className="px-4 py-3">Source</th>
                        <th className="px-4 py-3">Reg. Number</th>
                        <th className="px-4 py-3">Contact</th>
                        <th className="px-4 py-3">Industry</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Registered</th>
                        <th className="px-4 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!companies.length && <EmptyRow colSpan={8} loading={companiesLoading} label="No companies found." />}
                      {companies.map((c) => {
                        const fromWebsite = c.source === 'website';
                        return (
                          <tr key={c._id} className={tbodyRowClass}>
                            <td className="px-4 py-3">
                              <p className="text-slate-900 text-xs font-medium">{c.companyName}</p>
                              <p className="text-slate-500 text-xs">{c.email}</p>
                            </td>
                            <td className="px-4 py-3">
                              <Badge tone={fromWebsite ? 'info' : 'neutral'}>{fromWebsite ? 'Website Signup' : 'Registered'}</Badge>
                            </td>
                            <td className="px-4 py-3 font-mono text-slate-500 text-xs">{c.registrationNumber || '—'}</td>
                            <td className="px-4 py-3">
                              <p className="text-slate-900 text-xs">{c.contactPerson}</p>
                              {fromWebsite ? (
                                <p className="text-slate-400 text-xs">{c.userCount} user{c.userCount === 1 ? '' : 's'} signed up</p>
                              ) : (
                                c.contactPersonTitle && <p className="text-slate-400 text-xs">{c.contactPersonTitle}</p>
                              )}
                            </td>
                            <td className="px-4 py-3 text-slate-500 text-xs">{c.industry || '—'}</td>
                            <td className="px-4 py-3">
                              {fromWebsite ? (
                                <Badge tone="neutral">Not Registered</Badge>
                              ) : (
                                <Badge tone={c.status === 'ACTIVE' ? 'success' : 'neutral'}>{c.status}</Badge>
                              )}
                            </td>
                            <td className="px-4 py-3 text-slate-500 text-xs">{new Date(c.createdAt).toLocaleDateString()}</td>
                            <td className="px-4 py-3">
                              {fromWebsite ? (
                                <button
                                  type="button"
                                  onClick={() => startRegisterFromWebsite(c)}
                                  className={btnGhostNeutral}
                                >
                                  Register
                                </button>
                              ) : (
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={() => startEditCompany(c)}
                                    className={btnGhostNeutral}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setConfirmDeleteCompanyId(c._id)}
                                    className={btnGhostDanger}
                                  >
                                    Delete
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {companiesTotalPages > 1 && (
                  <Pagination
                    page={companiesPage}
                    totalPages={companiesTotalPages}
                    loading={companiesLoading}
                    onPrev={() => loadCompanies(Math.max(companiesPage - 1, 1))}
                    onNext={() => loadCompanies(Math.min(companiesPage + 1, companiesTotalPages))}
                  />
                )}
              </div>
            )}

            {/* ════ AUDIT LOGS ══════════════════════════════════════════════ */}
            {activeTab === 'audit' && (
              <div>
                <PageHeader title="Audit Logs" subtitle="Admin login events & security activity">
                  <button type="button" onClick={exportAuditCsv} className={btnSecondary}>
                    Export CSV
                  </button>
                </PageHeader>

                <div className={`p-4 mb-5 ${cardClass}`}>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                    <input type="text" value={auditIp} onChange={(e) => setAuditIp(e.target.value)} placeholder="Filter by IP" className={inputClass} />
                    <input type="text" value={auditCountry} onChange={(e) => setAuditCountry(e.target.value)} placeholder="Filter by country" className={inputClass} />
                    <input type="text" value={auditLocation} onChange={(e) => setAuditLocation(e.target.value)} placeholder="Filter by city/region" className={inputClass} />
                  </div>
                  <button type="button" onClick={() => loadAuditLogs(1)} disabled={auditLoading} className={btnPrimary}>
                    {auditLoading ? 'Loading…' : 'Apply Filters'}
                  </button>
                </div>

                <div className={tableWrapClass}>
                  <table className="w-full min-w-[820px] text-sm">
                    <thead>
                      <tr className={theadRowClass}>
                        <th className="px-4 py-3">Time</th>
                        <th className="px-4 py-3">Event</th>
                        <th className="px-4 py-3">Username</th>
                        <th className="px-4 py-3">Result</th>
                        <th className="px-4 py-3">Reason</th>
                        <th className="px-4 py-3">IP</th>
                        <th className="px-4 py-3">Country</th>
                        <th className="px-4 py-3">City</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!auditLogs.length && <EmptyRow colSpan={8} loading={auditLoading} label="No logs found." />}
                      {auditLogs.map((log) => (
                        <tr key={log._id} className={tbodyRowClass}>
                          <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(log.createdAt)}</td>
                          <td className="px-4 py-3 font-mono text-slate-500 text-xs">{log.eventType}</td>
                          <td className="px-4 py-3 text-slate-900">{log.username || '-'}</td>
                          <td className="px-4 py-3">
                            <Badge tone={log.success ? 'success' : 'danger'}>{log.success ? 'SUCCESS' : 'FAILED'}</Badge>
                          </td>
                          <td className="px-4 py-3 text-slate-500 text-xs">{log.reason || '-'}</td>
                          <td className="px-4 py-3 font-mono text-slate-500 text-xs">{log.ipAddress || '-'}</td>
                          <td className="px-4 py-3 text-slate-500">{log.metadata?.country || '-'}</td>
                          <td className="px-4 py-3 text-slate-500">{log.metadata?.city || log.metadata?.region || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination page={auditPage} totalPages={auditTotalPages} loading={auditLoading} onPrev={() => loadAuditLogs(Math.max(auditPage - 1, 1))} onNext={() => loadAuditLogs(Math.min(auditPage + 1, auditTotalPages))} />
              </div>
            )}

            {/* ════ SETTINGS ════════════════════════════════════════════════ */}
            {activeTab === 'settings' && (
              <div className="max-w-2xl">
                <h1 className="text-2xl font-semibold text-slate-900 mb-6">Settings</h1>

                <div className="space-y-5">
                  {/* My profile */}
                  <div className={`p-6 ${cardClass}`}>
                    <h2 className="text-base font-semibold text-slate-900 mb-0.5">My Profile</h2>
                    <p className="text-slate-500 text-sm mb-4">Email also doubles as your login username — changing it signs you out everywhere.</p>
                    <div className="space-y-3">
                      <input type="email" value={profileEmail} onChange={(e) => setProfileEmail(e.target.value)} placeholder="Email" className={inputClass} />
                      <input type="tel" value={profilePhone} onChange={(e) => setProfilePhone(e.target.value)} placeholder="Phone number" className={inputClass} />
                      <button type="button" onClick={updateProfile} disabled={profileLoading} className={btnPrimary}>
                        {profileLoading ? 'Saving…' : 'Save Profile'}
                      </button>
                    </div>
                  </div>

                  {/* 2FA */}
                  <div className={`p-6 ${cardClass}`}>
                    <h2 className="text-base font-semibold text-slate-900 mb-0.5">Two-Factor Authentication</h2>
                    <p className="text-slate-500 text-sm mb-4">Protect admin login with an authenticator app.</p>
                    <div className="flex items-center gap-3 mb-4">
                      <Badge tone={authUser?.twoFactorEnabled ? 'success' : 'warning'}>
                        {authUser?.twoFactorEnabled ? '2FA ENABLED' : '2FA DISABLED'}
                      </Badge>
                      <button type="button" onClick={setupTwoFactor} disabled={twoFaSetupLoading} className={btnPrimary}>
                        {twoFaSetupLoading ? 'Preparing…' : 'Generate QR'}
                      </button>
                    </div>
                    {twoFaSetupUrl && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                          <p className="text-xs text-slate-400 uppercase tracking-widest mb-3">Scan with authenticator</p>
                          <img src={`https://quickchart.io/qr?size=200&text=${encodeURIComponent(twoFaSetupUrl)}`} alt="2FA QR" className="w-[200px] h-[200px] rounded-lg bg-white p-2 border border-slate-200" />
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                          <p className="text-xs text-slate-400 uppercase tracking-widest mb-2">Backup secret</p>
                          <p className="font-mono text-slate-600 text-xs break-all mb-4">{twoFaSetupSecret}</p>
                          <input type="text" value={twoFaCode} onChange={(e) => setTwoFaCode(e.target.value)} placeholder="Enter 6-digit OTP to confirm" className={`${inputClass} mb-3`} />
                          <button type="button" onClick={verifyTwoFactor} disabled={twoFaVerifyLoading} className={`w-full ${btnPrimary}`}>
                            {twoFaVerifyLoading ? 'Verifying…' : 'Verify & Enable'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Change password */}
                  <div className={`p-6 ${cardClass}`}>
                    <h2 className="text-base font-semibold text-slate-900 mb-0.5">Change Password</h2>
                    <p className="text-slate-500 text-sm mb-4">All sessions will be signed out after a password change.</p>
                    <div className="space-y-3">
                      <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Current password" className={inputClass} />
                      <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New password (min 10 chars)" className={inputClass} />
                      <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password" className={inputClass} />
                      <button type="button" onClick={changePassword} disabled={passwordChangeLoading} className={btnPrimary}>
                        {passwordChangeLoading ? 'Updating…' : 'Update Password'}
                      </button>
                    </div>
                    {passwordChangeMessage && <p className="mt-4 text-emerald-600 text-sm">{passwordChangeMessage}</p>}
                  </div>

                  {/* Add admin */}
                  <div className={`p-6 ${cardClass}`}>
                    <h2 className="text-base font-semibold text-slate-900 mb-0.5">Add Admin</h2>
                    <p className="text-slate-500 text-sm mb-4">Create another admin account with login access.</p>
                    <div className="space-y-3">
                      <input type="email" value={newAdminEmail} onChange={(e) => setNewAdminEmail(e.target.value)} placeholder="Email" className={inputClass} />
                      <input type="tel" value={newAdminPhone} onChange={(e) => setNewAdminPhone(e.target.value)} placeholder="Phone number" className={inputClass} />
                      <input type="password" value={newAdminPassword} onChange={(e) => setNewAdminPassword(e.target.value)} placeholder="Password (min 10 chars)" className={inputClass} />
                      <input type="password" value={newAdminConfirmPassword} onChange={(e) => setNewAdminConfirmPassword(e.target.value)} placeholder="Confirm password" className={inputClass} />
                      <button type="button" onClick={createAdmin} disabled={createAdminLoading} className={btnPrimary}>
                        {createAdminLoading ? 'Creating…' : 'Create Admin'}
                      </button>
                    </div>
                    {createAdminMessage && <p className="mt-4 text-emerald-600 text-sm">{createAdminMessage}</p>}
                  </div>

                  {/* Registered admins */}
                  <div className={`p-6 ${cardClass}`}>
                    <div className="flex items-center justify-between mb-0.5">
                      <h2 className="text-base font-semibold text-slate-900">Registered Admins</h2>
                      <button type="button" onClick={loadAdmins} disabled={adminsLoading} className="text-xs text-indigo-600 hover:text-indigo-700 font-medium disabled:opacity-60">
                        {adminsLoading ? 'Loading…' : 'Refresh'}
                      </button>
                    </div>
                    <p className="text-slate-500 text-sm mb-4">All accounts with access to this dashboard.</p>
                    <div className="space-y-2">
                      {!adminsList.length && (
                        <p className="text-slate-400 text-sm py-4 text-center">{adminsLoading ? 'Loading…' : 'No admins found.'}</p>
                      )}
                      {adminsList.map((a) => (
                        <div key={a._id} className="rounded-lg border border-slate-200 bg-slate-50 p-3.5">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div>
                              <p className="text-slate-900 text-sm font-medium">{a.email || a.username}</p>
                              <p className="text-slate-500 text-xs">{a.phone || 'No phone on file'}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge tone={a.isActive ? 'success' : 'danger'}>{a.isActive ? 'Active' : 'Inactive'}</Badge>
                              <Badge tone={a.twoFactorEnabled ? 'info' : 'neutral'}>{a.twoFactorEnabled ? '2FA On' : '2FA Off'}</Badge>
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-2">
                            <p className="text-slate-400 text-xs">
                              Last login: {a.lastLoginAt ? formatDate(a.lastLoginAt) : 'Never'} · Created: {formatDate(a.createdAt)}
                            </p>
                            {a._id !== authUser?.userId && (
                              <button
                                type="button"
                                onClick={() => setConfirmRemoveAdminId(a._id)}
                                className={btnGhostDanger}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        </main>
      </div>

      {/* ── Confirmation modals ──────────────────────────────────────────── */}
      {confirmCancelId && (
        <ConfirmModal
          title="Cancel this invoice?"
          description="This cannot be undone."
          confirmLabel="Yes, cancel"
          onCancel={() => setConfirmCancelId(null)}
          onConfirm={() => cancelInvoice(confirmCancelId)}
        />
      )}

      {confirmDeleteCompanyId && (
        <ConfirmModal
          title="Delete this company?"
          description="This cannot be undone."
          confirmLabel="Yes, delete"
          onCancel={() => setConfirmDeleteCompanyId(null)}
          onConfirm={() => deleteCompany(confirmDeleteCompanyId)}
        />
      )}

      {confirmRemoveAdminId && (
        <ConfirmModal
          title="Remove this admin?"
          description="They will lose access to this dashboard immediately. This cannot be undone."
          confirmLabel="Yes, remove"
          onCancel={() => setConfirmRemoveAdminId(null)}
          onConfirm={() => removeAdmin(confirmRemoveAdminId)}
        />
      )}
    </div>
  );
};

export default AdminPaymentsPage;
